import { HEN_BREEDS, BREED_IDS } from '../../data/breeds';
import { ROOSTER_BREEDS } from '../../data/roosters';
import { TRAITS } from '../../data/misc';
import { formatMoney, formatRate } from '../../core/format';
import { unlockRuleText } from '../../core/requirements';
import { HEN_SPECIALS, ROOSTER_PASSIVES } from '../../core/specials';
import { game } from '../controller';
import { h } from '../dom';
import { henPortrait, roosterPortrait } from '../portrait';
import { card, section, tag, type Panel } from '../shop';

const HOW_TO: string[] = [
  'Click hens with eggs in their nest to collect them. Each egg sells instantly.',
  'Buy nesting boxes and fill them with hens. The first hen of each breed in a flock earns +75%.',
  'Buy an Egg Basket (Farm tab) so eggs get gathered automatically — that is also how you earn while away.',
  'At 5 hens you can hire your first rooster. Click him to crow: his whole flock lays faster for a while.',
  'Stage 1: bank $1,000,000 with 15 hens and a rooster. Then the long game begins: up to ten roosters, each with its own flock.',
  'Keep clicking fast (3+ clicks a second) to build a combo: clicked eggs are worth up to 2×, and you bank extra offline time.',
  'Right-click a hen to release her. Space makes the rooster in view crow.',
];

const EVENTS_HELP: [string, string][] = [
  ['🥚 Yolk Burst', 'Collects every egg in every nest at double value.'],
  ['🌧️ Egg Shower', 'Rains golden storm eggs over the farm for a few seconds — click them.'],
  ['⏩ Double Time', 'Hens lay twice as fast for 30 seconds.'],
  ['🥈 Silver egg', 'A payout. With a golden egg on screen it becomes a Silver Storm: a bigger payout and 3× gathered eggs.'],
  ['🥇 Golden egg', 'Egg Burst (big payout), Laying Frenzy (3× laying), Lucky Clutch (next 10 clicked eggs 3×) or Egg Storm (5× clicks + storm eggs).'],
  ['🌈 Rainbow egg', 'Needs an Easter Egger. Fills nests, boosts laying and gathering, or supercharges clicks.'],
  ['🤢 Rotten egg', 'Click to compost it for a little cash. Ignore it and egg value drops 15% for 30 seconds.'],
  ['🐣 Rooster egg', 'Appears once you own a rooster. Keep it warm and it hatches a cockerel: your next rooster costs 25% less (up to 50%).'],
  ['🪺 Broody hen', 'Broody-prone breeds sometimes want to sit. Let her brood for a brood-egg reward and a chance at a permanent matriarch trait, or shoo her back to laying.'],
];

export const helpPanel: Panel = {
  id: 'help',
  label: 'Help',
  key: () => `${game.state.unlockedBreeds.length}`,
  build(root: HTMLElement) {
    const s = game.state;
    const how = section('📖 How to play');
    how.append(h('ul', {}, ...HOW_TO.map((t) => h('li', { class: 'desc', style: 'margin-bottom:4px' }, t))));
    root.append(how);

    const ev = section('✨ Special eggs & events');
    for (const [name, text] of EVENTS_HELP) ev.append(h('div', { class: 'kv' }, h('span', {}, h('b', {}, name), ` — ${text}`)));
    root.append(ev);

    const tr = section('👑 Matriarch traits', 'earned from successful broods');
    for (const t of Object.values(TRAITS)) tr.append(h('div', { class: 'kv' }, h('span', {}, h('b', {}, t.name), ` — ${t.description}`)));
    root.append(tr);

    const hens = section('🐔 Hen breeds', `${s.unlockedBreeds.length}/${BREED_IDS.length} unlocked`);
    for (const id of BREED_IDS) {
      const b = HEN_BREEDS[id];
      const unlocked = s.unlockedBreeds.includes(id);
      const sp = b.special ? HEN_SPECIALS[b.special] : null;
      hens.append(card({
        cls: unlocked ? '' : 'locked',
        icon: henPortrait(id),
        title: [b.name, tag(b.tier, `tier-${b.tier}`), b.broodyProne ? tag('broody') : null].filter(Boolean) as (Node | string)[],
        desc: `${b.layRate.toFixed(2)} eggs/s base · ${formatRate(b.eggValue)}/egg · ${formatMoney(b.cost)} · ${unlocked ? 'unlocked' : unlockRuleText(b.unlock)}`,
        meta: sp ? `${sp.name}: ${sp.description}` : 'No special ability — just solid laying.',
        flavor: `${b.flavor} ${b.birdFact}`,
      }));
    }
    root.append(hens);

    const roos = section('🐓 Rooster breeds');
    for (const id of BREED_IDS) {
      const r = ROOSTER_BREEDS[id];
      roos.append(card({
        icon: roosterPortrait(id),
        title: [r.name],
        desc: ROOSTER_PASSIVES[r.passive].description(r.aura),
        meta: `Crow ×${r.crowPower} laying for ${r.crowDurationSec}s every ${Math.round(r.crowCooldownSec / 60)} min${r.broodyMod !== 1 ? ` · broody pace ×${r.broodyMod}` : ''}`,
        flavor: r.birdFact,
      }));
    }
    root.append(roos);
  },
};
